import { MapClickContext, RequestGeoJsonGeometry } from './map-click-context.models';

/** Half-width in degrees of the square searched around a clicked point (about 500 m). */
export const POINT_AREA_HALF_WIDTH_DEGREES = 0.005;

/** The form starts only the metadata search; downloading found products is a separate existing process. */
export const FIND_PRODUCTS_PROCESS_KEY = 'satellite.find-products';

const LAT_LIMIT = 90;
const LNG_LIMIT = 180;
const COORDINATE_NAME_DECIMALS = 4;

const PROVIDER_BY_SATELLITE: Readonly<Record<string, string>> = {
  sentinel: 'Sentinel2',
  landsat: 'Landsat',
};

/** Input of the `satellite.find-products` process. */
export interface SatelliteProcessParameters {
  Name: string;
  Geometry: { SourceType: 'GeoJson'; GeoJson: string };
  Providers: string;
  StartDate: string;
  EndDate: string;
  Priority: 'Normal';
}

export interface ProcessingRequestInput {
  /** Form value: `sentinel` or `landsat`. */
  satellite: string;
  date: Date;
  context: MapClickContext;
  /** Injected for tests; defaults to the current time. */
  now?: Date;
}

/** Why the process input could not be built; lets the caller pick a specific message. */
export type ProcessingRequestErrorCode = 'invalid-coordinates' | 'unknown-satellite';

export class ProcessingRequestError extends Error {
  constructor(readonly code: ProcessingRequestErrorCode, message: string) {
    super(message);
    this.name = 'ProcessingRequestError';
  }
}

/** True for finite coordinates within lat -90..90 and lng -180..180. */
export function isValidMapCoordinate(lat: unknown, lng: unknown): boolean {
  return (
    typeof lat === 'number' &&
    typeof lng === 'number' &&
    Number.isFinite(lat) &&
    Number.isFinite(lng) &&
    Math.abs(lat) <= LAT_LIMIT &&
    Math.abs(lng) <= LNG_LIMIT
  );
}

/** Square polygon (closed ring, `[lng, lat]`) centred on the point. */
export function pointToSquareGeometry(lat: number, lng: number): RequestGeoJsonGeometry {
  const d = POINT_AREA_HALF_WIDTH_DEGREES;
  const ring = [
    [lng - d, lat - d],
    [lng + d, lat - d],
    [lng + d, lat + d],
    [lng - d, lat + d],
    [lng - d, lat - d],
  ];
  return { type: 'Polygon', coordinates: [ring] };
}

function pad(value: number): string {
  return String(value).padStart(2, '0');
}

/** Local calendar date as `yyyy-MM-dd`. */
export function formatRequestDate(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function startOfDay(date: Date): number {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

/** Formats a coordinate pair for display and process names, e.g. `41.2995, 69.2401`. */
export function formatCoordinatePair(lat: number, lng: number): string {
  return `${lat.toFixed(COORDINATE_NAME_DECIMALS)}, ${lng.toFixed(COORDINATE_NAME_DECIMALS)}`;
}

/** Builds the find process input. Throws {@link ProcessingRequestError} on an unknown satellite or invalid coordinates. */
export function buildProcessingRequestParameters(input: ProcessingRequestInput): SatelliteProcessParameters {
  const provider = PROVIDER_BY_SATELLITE[input.satellite];
  if (!provider) {
    throw new ProcessingRequestError('unknown-satellite', `Unknown satellite: ${input.satellite}`);
  }
  const { lat, lng, field } = input.context;
  if (!isValidMapCoordinate(lat, lng)) {
    throw new ProcessingRequestError('invalid-coordinates', 'Coordinates are outside the valid range');
  }

  const geometry = field?.boundary ?? pointToSquareGeometry(lat, lng);
  const today = input.now ?? new Date();
  const end = startOfDay(input.date) < startOfDay(today) ? today : input.date;
  const pair = formatCoordinatePair(lat, lng);

  return {
    Name: field ? `Map request ${field.name} (${pair})` : `Map request ${pair}`,
    Geometry: { SourceType: 'GeoJson', GeoJson: JSON.stringify(geometry) },
    Providers: provider,
    StartDate: formatRequestDate(input.date),
    EndDate: formatRequestDate(end),
    Priority: 'Normal',
  };
}
